import { zodResolver } from '@hookform/resolvers/zod';
import { useRouter } from '@tanstack/react-router';
import { Search } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import { Button } from '@/components/ui/button';
import { Form, FormControl, FormField, FormItem } from '@/components/ui/form';
import { Input } from '@/components/ui/input';

import { ChangesIntervalPicker } from './ChangesIntervalPicker';
import {
  defaultValues,
  type FormValues,
  validationSchema,
} from './validationSchema';

interface Props {
  isSearching: boolean;
  initialValues: Partial<FormValues>;
}

export const SearchBar = ({
  isSearching,
  initialValues,
}: Props): JSX.Element => {
  const { formatMessage } = useIntl();
  const router = useRouter();

  const form = useForm<FormValues>({
    resolver: zodResolver(validationSchema),
    defaultValues: {
      ...defaultValues,
      ...initialValues,
    },
  });

  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(values => {
          router.navigate({
            to: '/',
            search: {
              searchTerm: values.searchTerm,
              from: values.from?.toISOString(),
              to: values.to?.toISOString(),
              showDeleted: values.showDeleted || undefined,
            },
          });
        })}
        className="flex w-full max-w-xl items-center space-x-2"
      >
        <FormField
          control={form.control}
          name="searchTerm"
          render={({ field }) => (
            <FormItem className="w-full">
              <FormControl>
                <div className="relative flex items-center w-full">
                  <Search className="absolute ml-2 text-gray-400" />
                  <Input
                    {...field}
                    placeholder={formatMessage({
                      id: 'search.form.searchTerm.placeholder',
                    })}
                    className="pl-12 w-full"
                    disabled={isSearching}
                    onChange={e => {
                      field.onChange(e);
                    }}
                  />
                </div>
              </FormControl>
            </FormItem>
          )}
        />
        <Button variant="outline" disabled={isSearching} type="submit">
          {isSearching ? (
            <FormattedMessage id="search.form.submit.pending" />
          ) : (
            <FormattedMessage id="search.form.submit.label" />
          )}
        </Button>
        <ChangesIntervalPicker />
      </form>
    </Form>
  );
};
